'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useRouter } from '@/app/navigation/routerProgress';
import { isAuthenticated } from '@/lib/auth-utils';

interface AuthWrapperProps {
  children: React.ReactNode;
}

// Routes that don't require authentication - exact and sub-paths
const PUBLIC_ROUTES = [
  '/login',
  '/customer/motor-doc-upload', // Public document upload page
  '/public-document-upload', // Public document upload alias used by request links
  '/dashboard/customer/motor-doc-upload', // Also public from dashboard path
  '/dashboard/approved/motor', // Motor approval page (with quotationNo parameter)
  '/dashboard/request-for-further-discount/fire-dwelling', // Fire dwelling discount request page
  '/dashboard/request-for-further-discount/motor', // Motor discount request page
  '/document/view', // Public SME token document viewers (/document/view/covernote/{token}, /document/view/quotation/{token})
];

// Routes that should redirect to login if not authenticated
const PROTECTED_ROUTES = [
  '/dashboard/request-credit-approval',
  '/dashboard', // Root dashboard (exact match)
  '/dashboard/products',
  '/dashboard/profile', // Dashboard profile section
  '/dashboard/quotation',
  '/dashboard/settings',
  '/dashboard/customer', // Protect customer routes except motor-doc-upload
  '/profile', // Standalone profile page
];

export default function AuthWrapper({ children }: AuthWrapperProps) {
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const checkAuth = () => {
      // Add debugging
      console.log('🔍 AuthWrapper - Checking path:', pathname);
      
      // Check if current path is public (exact matching for specific routes)
      const isPublicRoute = PUBLIC_ROUTES.some(route => {
        // For specific routes, check exact match or if it starts with the route + query params
        const match = pathname === route || 
                     (pathname.startsWith(route) && (pathname === route || pathname.charAt(route.length) === '?' || pathname.charAt(route.length) === '/' || pathname.charAt(route.length) === '#'));
        console.log(`📍 Checking public route '${route}' against '${pathname}':`, match);
        return match;
      });
      
      console.log('✅ Is public route:', isPublicRoute);
      
      // If it's a public route, allow access immediately
      if (isPublicRoute) {
        console.log('🔓 Public route detected - allowing access without authentication');
        setIsLoading(false);
        return;
      }
      
      // Check authentication only for non-public routes
      const authenticated = isAuthenticated();
      console.log('🔐 Authentication status:', authenticated);
      
      // Check if current path requires authentication
      const isProtectedRoute = PROTECTED_ROUTES.some(route => {
        // Special handling for root dashboard - only exact match
        if (route === '/dashboard') {
          const match = pathname === '/dashboard';
          console.log(`🛡️ Checking protected route '${route}' (exact match) against '${pathname}':`, match);
          return match;
        }
        // For other routes, use startsWith to catch all sub-routes
        const match = pathname === route || pathname.startsWith(route + '/');
        console.log(`🛡️ Checking protected route '${route}' against '${pathname}':`, match);
        return match;
      });
      
      console.log('🛡️ Is protected route:', isProtectedRoute);

      if (!authenticated && isProtectedRoute) {
        // Not authenticated and trying to access protected route
        console.log('❌ Not authenticated, redirecting to login');
        const returnUrl =
          window.location.pathname + window.location.search;
        sessionStorage.setItem('postLoginRedirect', returnUrl);
        router.push('/login');
        return;
      }

      if (authenticated && pathname === '/login') {
        // Authenticated and on login page
        console.log('✅ Authenticated user on login page, redirecting');
        const savedUrl = sessionStorage.getItem('postLoginRedirect');
        sessionStorage.removeItem('postLoginRedirect');

        const targetUrl =
          savedUrl && savedUrl.startsWith('/')
            ? savedUrl
            : '/dashboard';
        router.replace(targetUrl);
        return;
      }

      console.log('✅ Access granted');
      setIsLoading(false);
    };

    // Run check after component mounts (client-side only)
    checkAuth();
  }, [pathname, router]);

  // Show loading during authentication check
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return <>{children}</>;
}
